import { state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";
import { HIDDEN_SYSTEM_LORE, dossierFor } from "../../loreCodex";
import { bestiaryRoster, entryFor, hasMet, metCount, totalKills } from "../../bestiary";
import { drawShipShape, npcShipShape, npcSubtypeKey } from "../shipCanvasIcon";
import { HIDDEN_SYSTEM_IDS } from "../../galaxyVisibility";
import { openBlueprint, type BlueprintSpec, type BlueprintFaction } from "../shipBlueprint";

// Trophy Room (16.82, Don's direction) -- the game's record of what the
// captain has FOUND, as distinct from the Captain's Lounge's record of
// what they've DONE. Reachable from the Lounge, which is itself reachable
// from every Station, every Base, and the Expedition View's Bridge icon.
//
// Two collections, both deliberately showing their locked slots rather
// than hiding them: a collection you can't see the shape of isn't a
// collection, it's a surprise. The captain should be able to stand here
// and know there are exactly 10 mysteries and 32 opponents, and how many
// of each they still haven't met.
//
// Everything shown is derived live from real recorded state -- sightings
// and kills from state.bestiary (written at the moment a hostile spawns
// or dies), discoveries from state.visitedSystemIds/detectedHiddenSystemIds
// (16.46's own fields, not a parallel copy). Nothing here is stored twice,
// so nothing here can disagree with what the game actually did.
export function renderTrophyRoomScreen(container: HTMLElement, ctx: ScreenContext): void {
  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  const roster = bestiaryRoster();
  const met = metCount(state.bestiary, roster);
  const found = HIDDEN_SYSTEM_LORE.filter((l) => state.visitedSystemIds.includes(l.systemId));
  // A hidden system that's been detected by scan but never reached (16.46's
  // 4th "signal" state) is a real middle rung: the captain knows something
  // is there and hasn't been yet. Worth showing as its own state rather
  // than lumping it in with the entirely unknown.
  const signalled = HIDDEN_SYSTEM_LORE.filter(
    (l) => !state.visitedSystemIds.includes(l.systemId) && state.detectedHiddenSystemIds.includes(l.systemId)
  );

  function mysteryCardHtml(index: number): string {
    const lore = HIDDEN_SYSTEM_LORE[index];
    const isFound = state.visitedSystemIds.includes(lore.systemId);
    const isSignal = !isFound && state.detectedHiddenSystemIds.includes(lore.systemId);
    if (isFound) {
      return `
        <div class="trophy-card">
          <div class="trophy-card-head">
            <div>
              <div class="trophy-card-name">${lore.name}</div>
              <div class="trophy-card-role">${lore.title}</div>
            </div>
          </div>
          <p class="trophy-body">${lore.body}</p>
        </div>
      `;
    }
    return `
      <div class="trophy-card locked">
        <div class="trophy-card-head">
          <div>
            <div class="trophy-card-name">${isSignal ? lore.name : `Mystery ${index + 1}`}</div>
            <div class="trophy-card-role">
              ${isSignal ? "Signal detected — never reached" : "Undiscovered"}
            </div>
          </div>
        </div>
        <p class="trophy-body">
          ${
            isSignal
              ? "Your scanner has picked this system out of the dark, but nothing in it is known until you actually make the jump."
              : "Unlisted on every chart. Scan from a neighbouring system to pick up its signal, then go and see what is being kept off the map."
          }
        </p>
      </div>
    `;
  }

  function opponentCardHtml(index: number): string {
    const row = roster[index];
    const seen = hasMet(state.bestiary, row.key);
    const tally = entryFor(state.bestiary, row.key);
    const dossier = dossierFor(row.subtype);
    const unit = row.unit;
    if (!seen) {
      return `
        <div class="trophy-card locked">
          <div class="trophy-card-head">
            <canvas data-trophy-icon="${index}" width="88" height="88"></canvas>
            <div>
              <div class="trophy-card-name">Unidentified contact</div>
              <div class="trophy-card-role">${unit.faction} &middot; ${unit.hullTier}</div>
            </div>
          </div>
          <p class="trophy-body">Never encountered. Meet one in space and its full record opens here.</p>
        </div>
      `;
    }
    return `
      <div class="trophy-card">
        <div class="trophy-card-head">
          <canvas data-trophy-icon="${index}" width="88" height="88"></canvas>
          <div>
            <div class="trophy-card-name">${unit.name}</div>
            <div class="trophy-card-role">${dossier?.role ?? `${unit.faction} unit`}</div>
          </div>
        </div>
        <div class="trophy-stats">
          <span>Hull</span><span>${Math.round(unit.hullHp).toLocaleString()}</span>
          <span>Shield</span><span>${Math.round(unit.shieldHp).toLocaleString()}</span>
          <span>EHP</span><span>${Math.round(row.ehp).toLocaleString()}</span>
          <span>DPS</span><span>${unit.dps.toFixed(1)}</span>
          <span>Repair</span><span>${unit.selfRemoteRepairHpPerS ? `${unit.selfRemoteRepairHpPerS.toFixed(1)}/s` : "none"}</span>
          <span>Tier</span><span>${unit.hullTier} &middot; ${unit.hullTierLabel}</span>
        </div>
        <div class="trophy-kills">
          ${tally.killed.toLocaleString()} destroyed &middot; ${tally.seen.toLocaleString()} encountered
        </div>
        <p class="trophy-body">${dossier?.body ?? ""}</p>
        <button type="button" class="sf-bp-trigger" data-bp-opponent="${index}">Blueprint</button>
      </div>
    `;
  }

  wrap.innerHTML = `
    <h1>Trophy Room</h1>
    <p class="dim">
      What you have found, as opposed to what you have done -- that record is next door in the Lounge.
      Nothing here is awarded; every entry is something you personally went and met.
    </p>

    <h2>Mysteries</h2>
    <p class="trophy-progress">
      <strong>${found.length} of ${HIDDEN_SYSTEM_LORE.length}</strong> unlisted systems reached${
        signalled.length > 0
          ? ` &middot; ${signalled.length} more showing a signal you haven't followed`
          : ""
      }.
      ${
        found.length === HIDDEN_SYSTEM_LORE.length
          ? "Every one of them found. Read them together and see what they add up to."
          : "They are never revealed by ordinary scanner range, at any hull tier -- only by scanning from a system that borders one."
      }
    </p>
    <div class="trophy-grid">
      ${HIDDEN_SYSTEM_LORE.map((_, i) => mysteryCardHtml(i)).join("")}
    </div>

    <h2>Opponents</h2>
    <p class="trophy-progress">
      <strong>${met} of ${roster.length}</strong> identified &middot; ${totalKills(state.bestiary).toLocaleString()} destroyed in total.
      ${
        met === roster.length
          ? "Every hull in known space, met and recorded."
          : "A contact is identified the moment it enters your scene -- you do not have to kill it to log it."
      }
    </p>
    <div class="trophy-grid">
      ${roster.map((_, i) => opponentCardHtml(i)).join("")}
    </div>
  `;

  // Real canvas silhouettes, drawn with the exact same shape language and
  // draw call the Expedition View uses (16.79/16.82's shared
  // drawShipShape) rather than a second icon system that could drift from
  // it. Locked entries deliberately still draw the true shape -- greyed
  // out via CSS filter, so an unmet opponent reads as a real silhouette
  // you can't identify yet, not as an empty box.
  wrap.querySelectorAll<HTMLCanvasElement>("[data-trophy-icon]").forEach((canvas) => {
    const row = roster[Number(canvas.dataset.trophyIcon)];
    const c2d = canvas.getContext("2d");
    if (!row || !c2d) return;
    // Nose-up rather than nose-right (the in-flight convention): a card is
    // read like a page, and every other ship icon in the DOM (16.78's
    // Shipyard/Fleet Roster) already points up.
    drawShipShape(
      c2d,
      canvas.width / 2,
      canvas.height / 2,
      canvas.width * 0.42,
      -Math.PI / 2,
      npcShipShape(row.unit.faction, row.unit.name, row.unit.hullTier)
    );
  });

  wrap.querySelectorAll<HTMLButtonElement>("[data-bp-opponent]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const row = roster[Number(btn.dataset.bpOpponent)];
      if (row) openBlueprint(opponentBlueprintSpec(row));
    });
  });

  const back = document.createElement("button");
  back.type = "button";
  back.textContent = "Back to the Lounge";
  back.addEventListener("click", () => ctx.navigate("lounge"));
  wrap.appendChild(back);
}

// Same key resolution drawShipShape/npcShipShape already use for the card's
// own canvas icon above -- a guardian's "Alien Elite (Ancient)" name
// resolves to the plain "Elite" shape/loadout, matching 16.70/16.82's
// existing rule that an Ancient reads as a tougher Elite, not a new hull.
function opponentBlueprintSpec(row: ReturnType<typeof bestiaryRoster>[number]): BlueprintSpec {
  const unit = row.unit;
  const key = npcSubtypeKey(unit.name, unit.faction);
  const dossier = dossierFor(row.subtype);
  return {
    key,
    faction: unit.faction as BlueprintFaction,
    displayName: unit.name,
    tier: unit.hullTier,
    drawingNo: `SF-${key.slice(0, 3).toUpperCase()}-${unit.hullTier}`,
    stats: [
      { label: "Role", value: dossier?.role ?? `${unit.faction} unit` },
      { label: "Hull HP", value: Math.round(unit.hullHp).toLocaleString() },
      { label: "Shield HP", value: Math.round(unit.shieldHp).toLocaleString() },
      { label: "Shield regen", value: `${unit.shieldRegenPerS.toFixed(1)}/s` },
      { label: "Repair", value: unit.selfRemoteRepairHpPerS ? `${unit.selfRemoteRepairHpPerS.toFixed(1)}/s` : "none" },
      { label: "DPS", value: unit.dps.toFixed(1) },
      { label: "EHP", value: Math.round(row.ehp).toLocaleString() }
    ]
  };
}

// Small summary for the Captain's Lounge's own entry point, so the Lounge
// can advertise what's in here without duplicating any of the logic.
export function trophySummary(): { mysteriesFound: number; mysteriesTotal: number; opponentsMet: number; opponentsTotal: number } {
  const roster = bestiaryRoster();
  return {
    mysteriesFound: HIDDEN_SYSTEM_IDS.filter((id) => state.visitedSystemIds.includes(id)).length,
    mysteriesTotal: HIDDEN_SYSTEM_IDS.length,
    opponentsMet: metCount(state.bestiary, roster),
    opponentsTotal: roster.length
  };
}
